/**
 * Master Taxonomy Registry — MeesterJay 4.1 MVP (Canonical)
 *
 * ============================================================
 * DEFINITIVE SOURCE OF TRUTH
 * ============================================================
 *
 * HIERARCHY: Module → Skill → SubSkill → FamilyId
 *
 * 10 Modules, ~47 Skills (module-scoped), 259 SubSkills (1:1 with Families), 16 Tiers
 *
 * Split from monolithic 2878-line file in R23 (DDD Fase 3).
 * Data is now per-module in content/taxonomy/data/*.ts
 * REK module added 17 maart 2026 (Fase 0).
 * 10 modules: GET, REK, MET, TIJD, GELD, SWW, SPR, TAA, STU, TOPO.
 *
 * This file:
 * - Combines all module data into the canonical TAXONOMY array
 * - Runs self-validation at module load
 * - Builds lookup maps (FAMILY_BY_ID, FAMILY_BY_SUBSKILL, etc.)
 * ============================================================
 */

import type { ModuleId } from '../types'
import { tierToNiveauCode, isValidTier, isTierNiveauSynced } from './tiers'
import { debug, error as logError } from '../../utils/logger'

import type { FamilyDefinition } from './types'
import { GET_FAMILIES } from './data/GET'
import { REK_FAMILIES } from './data/REK'
import { MET_FAMILIES } from './data/MET'
import { TIJD_FAMILIES } from './data/TIJD'
import { GELD_FAMILIES } from './data/GELD'
import { SWW_FAMILIES } from './data/SWW'
import { SPR_FAMILIES } from './data/SPR'
import { TAA_FAMILIES } from './data/TAA'
import { TOPO_FAMILIES } from './data/TOPO'
import { STU_FAMILIES } from './data/STU'

// Re-export the type for consumers
export type { FamilyDefinition } from './types'

// ============================================================
// MASTER TAXONOMY 4.1 — CANONICAL INDEX
// ============================================================

export const TAXONOMY: FamilyDefinition[] = [
  ...GET_FAMILIES,
  ...REK_FAMILIES,
  ...MET_FAMILIES,
  ...TIJD_FAMILIES,
  ...GELD_FAMILIES,
  ...SWW_FAMILIES,
  ...SPR_FAMILIES,
  ...TAA_FAMILIES,
  ...TOPO_FAMILIES,
  ...STU_FAMILIES,
]

// ============================================================
// SELF-VALIDATION (runs at module load)
// ============================================================
;(function validateTaxonomyIntegrity() {
  const errors: string[] = []
  const seenFamilyIds = new Set<string>()
  const seenSubSkills = new Set<string>()
  for (const family of TAXONOMY) {
    // Unique familyId
    if (seenFamilyIds.has(family.familyId)) {
      errors.push(`Duplicate familyId: ${family.familyId}`)
    }
    seenFamilyIds.add(family.familyId)

    // Unique subSkill
    if (seenSubSkills.has(family.subSkill)) {
      errors.push(
        `Duplicate subSkill: ${family.subSkill} (in ${family.familyId})`,
      )
    }
    seenSubSkills.add(family.subSkill)

    // Valid tier
    if (!isValidTier(family.tier)) {
      errors.push(`Invalid tier ${family.tier} for ${family.familyId}`)
    }

    // Tier ↔ levelCode sync
    if (!isTierNiveauSynced(family.tier, family.levelCode)) {
      const expected = tierToNiveauCode(family.tier)
      errors.push(
        `Tier/levelCode mismatch for ${family.familyId}: tier ${family.tier} should be "${expected}", got "${family.levelCode}"`,
      )
    }
  }
  if (errors.length > 0) {
    logError('🚨 TAXONOMY REGISTRY INTEGRITY ERRORS:')
    errors.forEach((e) => logError(`  ❌ ${e}`))
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        `Taxonomy registry has ${errors.length} integrity error(s). Fix before proceeding.`,
      )
    }
  } else {
    debug(
      `✅ Taxonomy Registry validated: ${TAXONOMY.length} families, ${seenFamilyIds.size} unique IDs`,
    )
  }
})()

// ============================================================
// Lookup Maps (built once at module load)
// ============================================================

/** Primary lookup: canonical 4.1 familyId → FamilyDefinition */
export const FAMILY_BY_ID = new Map<string, FamilyDefinition>(
  TAXONOMY.map((f) => [f.familyId, f]),
)

/** SubSkill → FamilyDefinition (1:1 in 4.1) */
export const FAMILY_BY_SUBSKILL = new Map<string, FamilyDefinition>(
  TAXONOMY.map((f) => [f.subSkill, f]),
)

/** Module-grouped families */
export const FAMILIES_BY_MODULE = new Map<ModuleId, FamilyDefinition[]>()

/**
 * Module-scoped skill lookup
 * Key format: "ModuleId::skill" (e.g., "Tijd::snelheid", "SpellingWW::leenwoorden")
 * This prevents skill name collisions across modules.
 */
export const FAMILIES_BY_MODULE_SKILL = new Map<string, FamilyDefinition[]>()
TAXONOMY.forEach((f) => {
  // By module
  if (!FAMILIES_BY_MODULE.has(f.module)) {
    FAMILIES_BY_MODULE.set(f.module, [])
  }
  FAMILIES_BY_MODULE.get(f.module)!.push(f)

  // By module-scoped skill
  const moduleScopedKey = `${f.module}::${f.skill}`
  if (!FAMILIES_BY_MODULE_SKILL.has(moduleScopedKey)) {
    FAMILIES_BY_MODULE_SKILL.set(moduleScopedKey, [])
  }
  FAMILIES_BY_MODULE_SKILL.get(moduleScopedKey)!.push(f)
})
